/**
 * Entity vocabulary shared by `Chip purpose="entity"`, `EntityMenu` and
 * `AnalystMarkdown`.
 *
 * The kind list is provisional: it covers what the product surfaces today, but
 * the canonical set belongs to the entity-resolution service and should be
 * agreed with its owners before anything depends on exhaustiveness. It is a
 * closed union rather than a string so that the defang rules below, and the
 * accessible-name labels, cannot silently miss a kind.
 */

export type EntityKind =
  // Network indicators
  | 'ip'
  | 'domain'
  | 'url'
  | 'email'
  // File and execution indicators
  | 'hash'
  | 'file'
  | 'process'
  // Principals
  | 'host'
  | 'user'
  | 'account'
  // Catalogue references
  | 'cve'
  | 'technique'
  | 'rule';

/** Intelligence verdict. Uses the status palette, never the severity scale. */
export type EntityVerdict = 'malicious' | 'suspicious' | 'benign' | 'unknown';

/**
 * Spoken form of each kind. This is what prefixes an entity's accessible name,
 * so a screen-reader user hears "IP address 203.0.113.47" rather than a bare
 * string of digits with no indication of what it is.
 */
export const ENTITY_KIND_LABEL: Record<EntityKind, string> = {
  ip: 'IP address',
  domain: 'Domain',
  url: 'URL',
  email: 'Email address',
  hash: 'File hash',
  file: 'File',
  process: 'Process',
  host: 'Host',
  user: 'User',
  account: 'Account',
  cve: 'CVE',
  technique: 'Technique',
  rule: 'Rule',
};

/**
 * Kinds for which a defanged copy is meaningful. Defanging a hash or a username
 * produces a string that is merely wrong, so the action is hidden rather than
 * offered and ignored.
 */
const DEFANGABLE: ReadonlySet<EntityKind> = new Set<EntityKind>(['ip', 'domain', 'url', 'email']);

export function isDefangable(kind: EntityKind): boolean {
  return DEFANGABLE.has(kind);
}

/**
 * Neutralise an indicator so it can be pasted into a ticket, a chat message or
 * an email without being turned into a live link, unfurled by a preview bot, or
 * rewritten by a security proxy.
 *
 * Follows the convention used by CyberChef and MISP: every dot is bracketed,
 * `http` becomes `hxxp`, and `@` is bracketed. Bracketing every dot rather than
 * only the last is what makes the result unambiguous to the analyst refanging
 * it at the other end.
 *
 * @example defangIndicator('203.0.113.47')     // '203[.]0[.]113[.]47'
 * @example defangIndicator('https://evil.com') // 'hxxps[://]evil[.]com'
 */
export function defangIndicator(value: string): string {
  return value
    .replace(/https?:\/\//gi, (scheme) =>
      scheme.replace(/^http/i, 'hxxp').replace('://', '[://]'),
    )
    .replace(/\./g, '[.]')
    .replace(/@/g, '[@]');
}
